import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Calendar } from '../../components/ui/calendar';
import { tr } from 'date-fns/locale';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Percent, Building2, ArrowDownCircle, ArrowUpCircle, Search, TrendingUp, TrendingDown, Wallet, Tag, Check, CalendarDays, Plus, Trash2, Banknote, ChevronDown, ChevronRight, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

const API = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value, currency = 'TL') => {
  const n = Number(value || 0);
  const s = n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const symbol = currency === 'TL' ? '₺' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency;
  return `${s} ${symbol}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('tr-TR');
  } catch {
    return dateStr;
  }
};

const BoardBirimFaizHesaplama = () => {
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [etiketler, setEtiketler] = useState([]);
  const [selectedEtiketIds, setSelectedEtiketIds] = useState([]);
  const [etiketModalOpen, setEtiketModalOpen] = useState(false);
  const [pendingEtiketIds, setPendingEtiketIds] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  // Faiz Takvimi
  const [faizTakvimi, setFaizTakvimi] = useState([]);
  const [faizModalOpen, setFaizModalOpen] = useState(false);
  const [faizRange, setFaizRange] = useState(undefined); // { from: Date, to: Date }
  const [faizAylikOran, setFaizAylikOran] = useState('');
  const [faizAciklama, setFaizAciklama] = useState('');
  const [faizSaving, setFaizSaving] = useState(false);

  // Çekilen Krediler
  const [krediModalOpen, setKrediModalOpen] = useState(false);
  const [expandedKrediDate, setExpandedKrediDate] = useState(null);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('fortis_token')}`,
    'Content-Type': 'application/json',
  });

  const fetchBranches = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/board/branches`, { headers: getAuthHeaders() });
      if (res.ok) setBranches(await res.json());
    } catch (e) {
      toast.error('Şubeler yüklenemedi');
    }
  }, []);

  const fetchEtiketler = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/accounting/hesap-kartlari`, { headers: getAuthHeaders() });
      if (res.ok) setEtiketler(await res.json());
    } catch (e) {
      toast.error('Etiketler yüklenemedi');
    }
  }, []);

  const fetchFaizTakvimi = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/board/faiz-takvimi`, { headers: getAuthHeaders() });
      if (res.ok) setFaizTakvimi(await res.json());
    } catch (e) {
      toast.error('Faiz takvimi yüklenemedi');
    }
  }, []);

  useEffect(() => {
    fetchBranches();
    fetchEtiketler();
    fetchFaizTakvimi();
  }, [fetchBranches, fetchEtiketler, fetchFaizTakvimi]);

  const fetchTransactions = useCallback(async (subeId, etiketIds) => {
    if (!subeId) return;
    setLoading(true);
    try {
      const qs = etiketIds && etiketIds.length > 0
        ? `?giris_etiket_ids=${encodeURIComponent(etiketIds.join(','))}`
        : '';
      const res = await fetch(`${API}/api/board/branch-transactions/${subeId}${qs}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) setData(await res.json());
      else {
        toast.error('Veri yüklenemedi');
        setData(null);
      }
    } catch (e) {
      toast.error('Bağlantı hatası');
      setData(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (selectedBranchId) fetchTransactions(selectedBranchId, selectedEtiketIds);
    else setData(null);
  }, [selectedBranchId, selectedEtiketIds, fetchTransactions]);

  const filtered = useMemo(() => {
    if (!data) return [];
    let list = data.transactions || [];
    if (typeFilter !== 'all') list = list.filter(t => t.tip === typeFilter);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(t =>
        (t.cari_hesap_adi || '').toLowerCase().includes(s) ||
        (t.hesap_adi || '').toLowerCase().includes(s) ||
        (t.etiket_adi || '').toLowerCase().includes(s) ||
        (t.aciklama || '').toLowerCase().includes(s) ||
        (t.seri_no || '').toLowerCase().includes(s) ||
        (t.job_konu || '').toLowerCase().includes(s)
      );
    }
    return list;
  }, [data, typeFilter, search]);

  // Verilen tarih için tanımlı günlük faiz oranını bul (Faiz Takvimi'nden)
  const getDailyRateForDate = useCallback((isoDate) => {
    if (!isoDate || !Array.isArray(faizTakvimi)) return 0;
    const d = isoDate.slice(0, 10);
    for (const t of faizTakvimi) {
      if (d >= (t.baslangic_tarihi || '') && d <= (t.bitis_tarihi || '')) {
        return Number(t.gunluk_oran || 0);
      }
    }
    return 0;
  }, [faizTakvimi]);

  // Bir kredinin verilen gündeki GÜNCEL ana parası (aylık rollover ile birlikte).
  // Draw day'den itibaren 30 günlük dönemler halinde ilerler; her dönem sonunda o döneme ait faiz ana paraya eklenir.
  const creditPrincipalOn = useCallback((drawIso, initialPrincipal, onDayIso) => {
    if (!drawIso || !onDayIso || onDayIso < drawIso) return 0;
    const addOne = (iso) => {
      const [yy, mm, dd] = iso.split('-').map(Number);
      const t = new Date(Date.UTC(yy, mm - 1, dd));
      t.setUTCDate(t.getUTCDate() + 1);
      return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
    };
    const computeEnd = (iso) => {
      const [y1, m1, d1] = iso.split('-').map(Number);
      if (!y1 || !m1 || !d1) return '';
      const tm = m1 === 12 ? 1 : m1 + 1;
      const ty = m1 === 12 ? y1 + 1 : y1;
      const dt = new Date(Date.UTC(ty, tm - 1, d1));
      if (dt.getUTCMonth() !== tm - 1) dt.setUTCDate(0);
      return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
    };
    let principal = Number(initialPrincipal || 0);
    let periodStart = drawIso;
    let safetyOuter = 0;
    while (safetyOuter < 120) {
      safetyOuter += 1;
      const periodEnd = computeEnd(periodStart);
      if (onDayIso <= periodEnd) return principal;
      // Rollover: bu dönemin faizini ana paraya ekle
      let cursor = periodStart;
      let periodInterest = 0;
      let safetyInner = 0;
      while (cursor <= periodEnd && safetyInner < 40) {
        const rate = getDailyRateForDate(cursor);
        periodInterest += (principal * rate) / 100;
        cursor = addOne(cursor);
        safetyInner += 1;
      }
      principal += periodInterest;
      periodStart = addOne(periodEnd);
    }
    return principal;
  }, [getDailyRateForDate]);

  // GERÇEK günlük agregatlar (sanal faiz çıkışları hariç) — kredi tespitinde kullanılır
  const realDailyAggAndCredits = useMemo(() => {
    if (!filtered || filtered.length === 0) return { credits: [], realKasaNetByDay: {}, realDayCikisByDay: {} };
    const asc = [...filtered].sort((a, b) => (a.tarih || '').localeCompare(b.tarih || ''));
    const validAsc = asc.filter(t => t.tarih && /^\d{4}-\d{2}-\d{2}/.test(t.tarih));
    if (validAsc.length === 0) return { credits: [], realKasaNetByDay: {}, realDayCikisByDay: {} };
    const firstDateIso = validAsc[0].tarih.slice(0, 10);
    const lastTxDateIso = validAsc[validAsc.length - 1].tarih.slice(0, 10);
    const today = new Date();
    const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const endDateIso = lastTxDateIso > todayIso ? lastTxDateIso : todayIso;
    const txByDate = {};
    validAsc.forEach(t => {
      const key = t.tarih.slice(0, 10);
      (txByDate[key] = txByDate[key] || []).push(t);
    });
    const addOneDay = (iso) => {
      const [y, m, d] = iso.split('-').map(Number);
      const dt = new Date(Date.UTC(y, m - 1, d));
      dt.setUTCDate(dt.getUTCDate() + 1);
      return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
    };
    const realKasaNetByDay = {};
    const realDayCikisByDay = {};
    const credits = [];
    let tempC = 0, tempG = 0;
    let c = firstDateIso;
    let s = 0;
    while (c <= endDateIso && s < 10000) {
      const dayTxs = txByDate[c] || [];
      let dC = 0, dG = 0;
      dayTxs.forEach(t => {
        const amt = Number(t.miktar || 0);
        if (t.tip === 'cikis') dC += amt;
        else if (t.tip === 'giris') dG += amt;
      });
      tempC += dC;
      tempG += dG;
      const kasaEnd = tempG - tempC;
      realKasaNetByDay[c] = kasaEnd;
      realDayCikisByDay[c] = dC;
      if (kasaEnd < 0 && dC > 0) {
        const p = Math.min(dC, Math.abs(kasaEnd));
        if (p > 0) credits.push({ drawDate: c, baslangicAnaPara: p });
      }
      c = addOneDay(c);
      s += 1;
    }
    return { credits, realKasaNetByDay, realDayCikisByDay };
  }, [filtered]);

  // Tablo satırları: İlk işlem gününden bugüne kadar HER GÜN için bir satır üretir.
  // FAİZ HESABI — KREDİ BAZLI (Çekilen Krediler mantığı):
  // Her gün kasa_net < 0 ve o gün harcama varsa yeni kredi doğar. Günlük faiz = aktif kredilerin
  // o günkü güncel ana paraları (aylık rollover dahil) × günlük oran / 100.
  // FAİZ DEVRİ: Bir kredinin 30 günlük dönemi dolduğunda, o döneme ait toplam faiz, o günün satırında
  // sanal bir "Faiz Devri" ÇIKIŞ satırı olarak eklenir (kasa_net düşer). Bir sonraki dönem ana paraya
  // dahil olmuş şekilde devam eder.
  const rowsWithCumulative = useMemo(() => {
    if (!filtered || filtered.length === 0) return [];

    const asc = [...filtered].sort((a, b) => {
      const ka = `${a.tarih || ''} ${a.saat || ''} ${a.created_at || ''}`;
      const kb = `${b.tarih || ''} ${b.saat || ''} ${b.created_at || ''}`;
      return ka.localeCompare(kb);
    });

    const validAsc = asc.filter(t => t.tarih && /^\d{4}-\d{2}-\d{2}/.test(t.tarih));
    if (validAsc.length === 0) return [];

    const firstDateIso = validAsc[0].tarih.slice(0, 10);
    const lastTxDateIso = validAsc[validAsc.length - 1].tarih.slice(0, 10);
    const today = new Date();
    const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const endDateIso = lastTxDateIso > todayIso ? lastTxDateIso : todayIso;

    const txByDate = {};
    validAsc.forEach(t => {
      const key = t.tarih.slice(0, 10);
      (txByDate[key] = txByDate[key] || []).push(t);
    });

    const addOneDay = (iso) => {
      const [y, m, d] = iso.split('-').map(Number);
      const dt = new Date(Date.UTC(y, m - 1, d));
      dt.setUTCDate(dt.getUTCDate() + 1);
      return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
    };
    const computeEnd = (iso) => {
      const [y1, m1, d1] = iso.split('-').map(Number);
      if (!y1 || !m1 || !d1) return '';
      const tm = m1 === 12 ? 1 : m1 + 1;
      const ty = m1 === 12 ? y1 + 1 : y1;
      const dt = new Date(Date.UTC(ty, tm - 1, d1));
      if (dt.getUTCMonth() !== tm - 1) dt.setUTCDate(0);
      return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
    };

    // === 1. AŞAMA: Kredileri realDailyAggAndCredits'ten al ===
    const credits = realDailyAggAndCredits.credits;

    // Her kredi için rollover olaylarını (ayı dolan dönemler) hesapla
    const rolloverEvents = {}; // tarih → [{ credit_draw_date, period_interest, period_no }]
    for (const credit of credits) {
      let principal = credit.baslangicAnaPara;
      let periodStart = credit.drawDate;
      let periodNo = 1;
      let safety = 0;
      while (safety < 120) {
        safety += 1;
        const rawPeriodEnd = computeEnd(periodStart);
        if (!rawPeriodEnd || rawPeriodEnd > todayIso) break; // dönem henüz dolmadı
        let pc = periodStart;
        let periodInterest = 0;
        let si = 0;
        while (pc <= rawPeriodEnd && si < 40) {
          const rate = getDailyRateForDate(pc);
          periodInterest += (principal * rate) / 100;
          pc = addOneDay(pc);
          si += 1;
        }
        if (!rolloverEvents[rawPeriodEnd]) rolloverEvents[rawPeriodEnd] = [];
        rolloverEvents[rawPeriodEnd].push({
          credit_draw_date: credit.drawDate,
          period_interest: periodInterest,
          period_no: periodNo,
        });
        principal += periodInterest;
        periodStart = addOneDay(rawPeriodEnd);
        periodNo += 1;
      }
    }

    // === 2. AŞAMA: Satırları oluştur (sanal faiz devri çıkışları dahil) ===
    let runningCikis = 0;
    let runningGiris = 0;
    const rows = [];
    let cursor = firstDateIso;
    let safety = 0;

    while (cursor <= endDateIso && safety < 10000) {
      const dayTxs = txByDate[cursor] || [];

      if (dayTxs.length > 0) {
        dayTxs.forEach((t, idx) => {
          const amt = Number(t.miktar || 0);
          if (t.tip === 'cikis') runningCikis += amt;
          else if (t.tip === 'giris') runningGiris += amt;
          rows.push({
            ...t,
            rowKey: `${t.tip}-${t.id}-${idx}`,
            is_empty_day: false,
            is_last_of_day: false,
            day_iso: cursor,
            kumulatif_cikis: runningCikis,
            kumulatif_giris: runningGiris,
            kasa_net: runningGiris - runningCikis,
            faiz: 0,
          });
        });
      } else {
        rows.push({
          rowKey: `empty-${cursor}`,
          tarih: cursor,
          day_iso: cursor,
          is_empty_day: true,
          is_last_of_day: false,
          kumulatif_cikis: runningCikis,
          kumulatif_giris: runningGiris,
          kasa_net: runningGiris - runningCikis,
          faiz: 0,
        });
      }

      // FAİZ DEVRİ ÇIKIŞLARI: Bugün ayı dolan krediler varsa sanal çıkış satırları ekle
      const events = rolloverEvents[cursor] || [];
      for (const ev of events) {
        if (ev.period_interest <= 0) continue;
        runningCikis += ev.period_interest;
        rows.push({
          rowKey: `virt-faiz-${cursor}-${ev.credit_draw_date}-${ev.period_no}`,
          id: `virt-faiz-${cursor}-${ev.credit_draw_date}-${ev.period_no}`,
          tarih: cursor,
          day_iso: cursor,
          tip: 'cikis',
          miktar: ev.period_interest,
          para_birimi: 'TL',
          is_empty_day: false,
          is_last_of_day: false,
          is_virtual_faiz_cikis: true,
          period_no: ev.period_no,
          kaynak_kredi_tarihi: ev.credit_draw_date,
          cari_hesap_adi: 'Banka Faiz Tahakkuku',
          hesap_adi: 'Faiz Devri',
          etiket_adi: `Faiz Devri (${ev.period_no}. Dönem)`,
          aciklama: `Kredi çekim tarihi ${ev.credit_draw_date} — ${ev.period_no}. dönem kapanışı (aylık faiz ana paraya devredildi)`,
          seri_no: '',
          saat: '',
          kumulatif_cikis: runningCikis,
          kumulatif_giris: runningGiris,
          kasa_net: runningGiris - runningCikis,
          faiz: 0,
        });
      }

      // Gün sonu satırını işaretle ve faiz hesapla
      const lastIdx = rows.length - 1;
      const lastRow = rows[lastIdx];
      lastRow.is_last_of_day = true;

      // Günün faizi: Aktif kredilerin o günkü (rollover aware) ana paraları × günlük oran
      const dailyRate = getDailyRateForDate(cursor);
      if (dailyRate > 0 && credits.length > 0) {
        let dayInterest = 0;
        let activePrincipalSum = 0;
        for (const c of credits) {
          if (c.drawDate > cursor) continue;
          const p = creditPrincipalOn(c.drawDate, c.baslangicAnaPara, cursor);
          activePrincipalSum += p;
          dayInterest += (p * dailyRate) / 100;
        }
        if (dayInterest > 0) {
          lastRow.faiz = dayInterest;
          lastRow.faiz_oran = dailyRate;
          lastRow.faiz_bazi = -activePrincipalSum;
        }
      }

      cursor = addOneDay(cursor);
      safety += 1;
    }

    // Kümülatif faizi hesapla (eski→yeni)
    let runningFaiz = 0;
    rows.forEach(r => {
      runningFaiz += Number(r.faiz || 0);
      r.kumulatif_faiz = runningFaiz;
    });

    return rows.reverse();
  }, [filtered, getDailyRateForDate, creditPrincipalOn, realDailyAggAndCredits]);

  // Toplam biriken faiz (gösterilen tüm günlerin faizlerinin toplamı)
  const grandTotalFaiz = useMemo(
    () => rowsWithCumulative.reduce((sum, r) => sum + Number(r.faiz || 0), 0),
    [rowsWithCumulative]
  );

  // Verilen çekilme tarihinden 1 ay sonrasının ISO tarihini döner (ay sonu clamp)
  const computeSonOdemeIso = useCallback((drawIso) => {
    if (!drawIso) return '';
    const [y1, m1, d1] = drawIso.split('-').map(Number);
    if (!y1 || !m1 || !d1) return '';
    const targetMonth1 = m1 === 12 ? 1 : m1 + 1;
    const targetYear = m1 === 12 ? y1 + 1 : y1;
    const dt = new Date(Date.UTC(targetYear, targetMonth1 - 1, d1));
    if (dt.getUTCMonth() !== targetMonth1 - 1) {
      dt.setUTCDate(0);
    }
    return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
  }, []);

  // İşleyen faiz + güncel ana para + devir sayısı hesaplar (aylık rollover ile).
  const computeIsleyenFaiz = useCallback((drawIso, initialPrincipal) => {
    const empty = { totalInterest: 0, currentPrincipal: Number(initialPrincipal || 0), devirSayisi: 0 };
    if (!drawIso) return empty;
    const todayObj = new Date();
    const todayIso = `${todayObj.getFullYear()}-${String(todayObj.getMonth() + 1).padStart(2, '0')}-${String(todayObj.getDate()).padStart(2, '0')}`;
    if (todayIso < drawIso) return empty;
    const addOne = (iso) => {
      const [yy, mm, dd] = iso.split('-').map(Number);
      const t = new Date(Date.UTC(yy, mm - 1, dd));
      t.setUTCDate(t.getUTCDate() + 1);
      return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
    };
    let totalInterest = 0;
    let currentPrincipal = Number(initialPrincipal || 0);
    let devirSayisi = 0;
    let periodStart = drawIso;
    let safetyOuter = 0;
    while (periodStart <= todayIso && safetyOuter < 120) {
      safetyOuter += 1;
      const rawPeriodEnd = (() => {
        const [y1, m1, d1] = periodStart.split('-').map(Number);
        const tm = m1 === 12 ? 1 : m1 + 1;
        const ty = m1 === 12 ? y1 + 1 : y1;
        const dt = new Date(Date.UTC(ty, tm - 1, d1));
        if (dt.getUTCMonth() !== tm - 1) dt.setUTCDate(0);
        return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
      })();
      const periodEnd = rawPeriodEnd <= todayIso ? rawPeriodEnd : todayIso;
      let cursor = periodStart;
      let periodInterest = 0;
      let safetyInner = 0;
      while (cursor <= periodEnd && safetyInner < 40) {
        const rate = getDailyRateForDate(cursor);
        periodInterest += (currentPrincipal * rate) / 100;
        cursor = addOne(cursor);
        safetyInner += 1;
      }
      totalInterest += periodInterest;
      if (rawPeriodEnd <= todayIso) {
        currentPrincipal += periodInterest;
        devirSayisi += 1;
        periodStart = addOne(rawPeriodEnd);
      } else {
        break;
      }
    }
    return { totalInterest, currentPrincipal, devirSayisi };
  }, [getDailyRateForDate]);

  // Çekilen Krediler: Kasa net eksi olan ve harcama yapılan günlerin toplamı. Her gün tek satır.
  // kasa_net ve toplam_harcama SANAL faiz çıkışları hariç — gerçek tx bazlı.
  const krediRows = useMemo(() => {
    const { credits, realKasaNetByDay } = realDailyAggAndCredits;
    const byDay = {};
    rowsWithCumulative.forEach(r => {
      if (r.is_empty_day) return;
      if (r.is_virtual_faiz_cikis) return; // sanal faiz devri çıkışları kredi oluşturmaz
      if (r.tip !== 'cikis') return;
      const day = (r.tarih || '').slice(0, 10);
      if (!day) return;
      if (!byDay[day]) byDay[day] = { tarih: day, toplam_harcama: 0, kasa_net: 0, islem_sayisi: 0 };
      byDay[day].toplam_harcama += Number(r.miktar || 0);
      byDay[day].islem_sayisi += 1;
    });
    // GERÇEK kasa_net'i kullan (sanal faiz çıkışları hariç)
    Object.keys(byDay).forEach(day => {
      byDay[day].kasa_net = realKasaNetByDay[day] ?? 0;
    });
    // Sadece tespit edilmiş kredi günlerini tut (credits array'den)
    const creditDays = new Set(credits.map(c => c.drawDate));
    const result = Object.values(byDay)
      .filter(d => creditDays.has(d.tarih))
      .sort((a, b) => b.tarih.localeCompare(a.tarih));
    result.forEach(r => {
      r.son_odeme_tarihi = computeSonOdemeIso(r.tarih);
      const baslangicAnaPara = Math.min(Number(r.toplam_harcama || 0), Math.abs(Number(r.kasa_net || 0)));
      r.baslangic_ana_para = baslangicAnaPara;
      const { totalInterest, currentPrincipal, devirSayisi } = computeIsleyenFaiz(r.tarih, baslangicAnaPara);
      r.isleyen_faiz = totalInterest;
      r.faiz_isleyen_kisim = currentPrincipal;
      r.devir_sayisi = devirSayisi;
      r.kredi_durumu = 'acik';
    });
    const total = result.length;
    result.forEach((r, idx) => { r.kredi_no = total - idx; });
    return result;
  }, [rowsWithCumulative, computeSonOdemeIso, computeIsleyenFaiz, realDailyAggAndCredits]);

  const toplamCekilenKredi = useMemo(
    () => krediRows.reduce((sum, r) => sum + r.toplam_harcama, 0),
    [krediRows]
  );

  const toplamIsleyenFaiz = useMemo(
    () => krediRows.reduce((sum, r) => sum + Number(r.isleyen_faiz || 0), 0),
    [krediRows]
  );

  const toplamFaizIsleyenKisim = useMemo(
    () => krediRows.reduce((sum, r) => sum + Number(r.faiz_isleyen_kisim || 0), 0),
    [krediRows]
  );

  // Bir kredi için günlük faiz dökümünü (aylık rollover dahil) üretir. Export ve render için ortak kaynak.
  const buildDailyFaizList = useCallback((drawIso, initialPrincipal) => {
    const list = [];
    if (!drawIso) return list;
    const todayObj = new Date();
    const todayIso = `${todayObj.getFullYear()}-${String(todayObj.getMonth() + 1).padStart(2, '0')}-${String(todayObj.getDate()).padStart(2, '0')}`;
    const addOne = (iso) => {
      const [yy, mm, dd] = iso.split('-').map(Number);
      const t = new Date(Date.UTC(yy, mm - 1, dd));
      t.setUTCDate(t.getUTCDate() + 1);
      return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
    };
    const computeEnd = (iso) => {
      const [y1, m1, d1] = iso.split('-').map(Number);
      if (!y1 || !m1 || !d1) return '';
      const tm = m1 === 12 ? 1 : m1 + 1;
      const ty = m1 === 12 ? y1 + 1 : y1;
      const dt = new Date(Date.UTC(ty, tm - 1, d1));
      if (dt.getUTCMonth() !== tm - 1) dt.setUTCDate(0);
      return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
    };
    let currentPrincipal = Number(initialPrincipal || 0);
    let periodStart = drawIso;
    let periodNum = 1;
    let runningFaiz = 0;
    let safetyOuter = 0;
    while (periodStart <= todayIso && safetyOuter < 120) {
      safetyOuter += 1;
      const rawPeriodEnd = computeEnd(periodStart);
      const periodEnd = rawPeriodEnd <= todayIso ? rawPeriodEnd : todayIso;
      let cursor = periodStart;
      let periodInterest = 0;
      let safetyInner = 0;
      while (cursor <= periodEnd && safetyInner < 40) {
        const rate = getDailyRateForDate(cursor);
        const gunlukFaiz = (currentPrincipal * rate) / 100;
        periodInterest += gunlukFaiz;
        runningFaiz += gunlukFaiz;
        list.push({
          key: `d-${cursor}-${periodNum}`,
          tarih: cursor,
          oran: rate,
          ana_para: currentPrincipal,
          gunluk_faiz: gunlukFaiz,
          kumulatif_faiz: runningFaiz,
          donem: periodNum,
        });
        cursor = addOne(cursor);
        safetyInner += 1;
      }
      if (rawPeriodEnd <= todayIso) {
        const eskiAnaPara = currentPrincipal;
        currentPrincipal += periodInterest;
        const yeniPeriodStart = addOne(rawPeriodEnd);
        list.push({
          key: `r-${rawPeriodEnd}`,
          is_rollover: true,
          tarih: rawPeriodEnd,
          donem: periodNum,
          yeni_donem: periodNum + 1,
          eski_ana_para: eskiAnaPara,
          eklenen_faiz: periodInterest,
          yeni_ana_para: currentPrincipal,
          yeni_donem_baslangic: yeniPeriodStart,
        });
        periodStart = yeniPeriodStart;
        periodNum += 1;
      } else {
        break;
      }
    }
    return list;
  }, [getDailyRateForDate]);

  // Export: Çekilen Krediler özet tablosu
  const exportSummaryToExcel = useCallback(() => {
    if (!krediRows || krediRows.length === 0) {
      toast.error('Dışarı aktarılacak kredi bulunamadı');
      return;
    }
    const sheetData = krediRows.map(r => ({
      'Kredi No': `#${r.kredi_no}`,
      'Kredi Çekilme Tarihi': formatDate(r.tarih),
      'Kredi Son Ödeme Tarihi': formatDate(r.son_odeme_tarihi),
      'Ana Para (Çekilen Kredi) ₺': Number(r.toplam_harcama || 0),
      'Faiz İşleyen Kısım ₺': Number(r.faiz_isleyen_kisim || 0),
      'İşleyen Faiz ₺': Number(r.isleyen_faiz || 0),
      'Devir Sayısı': Number(r.devir_sayisi || 0),
      'Kredi Durumu': r.kredi_durumu === 'kapatildi' ? 'Kapatıldı' : 'Açık',
    }));
    sheetData.push({
      'Kredi No': 'TOPLAM',
      'Kredi Çekilme Tarihi': '',
      'Kredi Son Ödeme Tarihi': '',
      'Ana Para (Çekilen Kredi) ₺': toplamCekilenKredi,
      'Faiz İşleyen Kısım ₺': toplamFaizIsleyenKisim,
      'İşleyen Faiz ₺': toplamIsleyenFaiz,
      'Devir Sayısı': '',
      'Kredi Durumu': '',
    });
    const ws = XLSX.utils.json_to_sheet(sheetData);
    ws['!cols'] = [{ wch: 10 }, { wch: 22 }, { wch: 22 }, { wch: 24 }, { wch: 22 }, { wch: 18 }, { wch: 14 }, { wch: 16 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cekilen Krediler');
    const subeAdi = (data?.sube_adi || 'Sube').replace(/[^a-zA-Z0-9_-]/g, '_');
    const todayStr = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `Cekilen_Krediler_${subeAdi}_${todayStr}.xlsx`);
    toast.success('Özet Excel indirildi');
  }, [krediRows, toplamCekilenKredi, toplamFaizIsleyenKisim, toplamIsleyenFaiz, data]);

  // Export: Tek bir kredinin günlük faiz dökümü
  const exportCreditDetailToExcel = useCallback((row) => {
    const list = buildDailyFaizList(row.tarih, row.baslangic_ana_para);
    if (list.length === 0) {
      toast.error('Günlük döküm oluşturulamadı');
      return;
    }
    const sheetData = list.map(d => {
      if (d.is_rollover) {
        return {
          'Tarih': formatDate(d.tarih),
          'Dönem': `D${d.donem} → D${d.yeni_donem} (ROLLOVER)`,
          'Ana Para ₺': '',
          'Günlük Oran %': '',
          'Günlük Faiz ₺': '',
          'Kümülatif Faiz ₺': '',
          'Not': `Dönem kapandı: ${formatCurrency(d.eski_ana_para)} + ${formatCurrency(d.eklenen_faiz)} = ${formatCurrency(d.yeni_ana_para)} (yeni ana para)`,
        };
      }
      return {
        'Tarih': formatDate(d.tarih),
        'Dönem': `D${d.donem}`,
        'Ana Para ₺': Number(d.ana_para || 0),
        'Günlük Oran %': Number(d.oran || 0),
        'Günlük Faiz ₺': Number(d.gunluk_faiz || 0),
        'Kümülatif Faiz ₺': Number(d.kumulatif_faiz || 0),
        'Not': '',
      };
    });
    const lastKumulatif = list.filter(x => !x.is_rollover).reduce((mx, x) => Math.max(mx, Number(x.kumulatif_faiz || 0)), 0);
    sheetData.push({
      'Tarih': 'TOPLAM',
      'Dönem': '',
      'Ana Para ₺': '',
      'Günlük Oran %': '',
      'Günlük Faiz ₺': '',
      'Kümülatif Faiz ₺': lastKumulatif,
      'Not': '',
    });
    const ws = XLSX.utils.json_to_sheet(sheetData);
    ws['!cols'] = [{ wch: 14 }, { wch: 22 }, { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 18 }, { wch: 60 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Kredi #${row.kredi_no}`);
    const subeAdi = (data?.sube_adi || 'Sube').replace(/[^a-zA-Z0-9_-]/g, '_');
    XLSX.writeFile(wb, `Kredi_${row.kredi_no}_${subeAdi}_${row.tarih}.xlsx`);
    toast.success(`Kredi #${row.kredi_no} detay Excel indirildi`);
  }, [buildDailyFaizList, data]);

  const grandTotalCikis = useMemo(
    () => filtered.filter(t => t.tip === 'cikis').reduce((sum, t) => sum + Number(t.miktar || 0), 0),
    [filtered]
  );

  const grandTotalGiris = useMemo(
    () => filtered.filter(t => t.tip === 'giris').reduce((sum, t) => sum + Number(t.miktar || 0), 0),
    [filtered]
  );

  const grandKasaNet = grandTotalGiris - grandTotalCikis;

  const openEtiketModal = () => {
    setPendingEtiketIds([...selectedEtiketIds]);
    setEtiketModalOpen(true);
  };

  const toggleEtiket = (id) => {
    setPendingEtiketIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const applyEtiketSelection = () => {
    setSelectedEtiketIds(pendingEtiketIds);
    setEtiketModalOpen(false);
  };

  const clearEtiketSelection = () => {
    setSelectedEtiketIds([]);
  };

  const selectedEtiketObjects = useMemo(() =>
    etiketler.filter(e => selectedEtiketIds.includes(e.id)),
    [etiketler, selectedEtiketIds]
  );

  // Faiz takvimi handlers
  const toIsoDate = (d) => {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const saveFaizEntry = async () => {
    if (!faizRange?.from || !faizRange?.to) {
      toast.error('Takvimden başlangıç ve bitiş tarihlerini seçin');
      return;
    }
    const aylikNum = parseFloat(faizAylikOran);
    if (!faizAylikOran || isNaN(aylikNum) || aylikNum < 0) {
      toast.error('Aylık faiz oranı girilmeli');
      return;
    }
    setFaizSaving(true);
    try {
      const res = await fetch(`${API}/api/board/faiz-takvimi`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          baslangic_tarihi: toIsoDate(faizRange.from),
          bitis_tarihi: toIsoDate(faizRange.to),
          aylik_oran: aylikNum,
          aciklama: faizAciklama || ''
        })
      });
      if (res.ok) {
        toast.success('Faiz oranı eklendi');
        setFaizRange(undefined);
        setFaizAylikOran('');
        setFaizAciklama('');
        fetchFaizTakvimi();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Kaydetme başarısız');
      }
    } catch (e) {
      toast.error('Bağlantı hatası');
    }
    setFaizSaving(false);
  };

  const deleteFaizEntry = async (id) => {
    if (!window.confirm('Bu faiz oranı tanımını silmek istiyor musunuz?')) return;
    try {
      const res = await fetch(`${API}/api/board/faiz-takvimi/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('Kayıt silindi');
        fetchFaizTakvimi();
      } else {
        toast.error('Silme başarısız');
      }
    } catch (e) {
      toast.error('Bağlantı hatası');
    }
  };

  return (
    <div className="space-y-6 p-4" data-testid="board-faiz-hesaplama">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Percent className="h-6 w-6 text-indigo-600" />
          Faiz Hesaplama
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setFaizModalOpen(true)}
            className="gap-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            data-testid="open-faiz-takvimi-btn"
          >
            <CalendarDays className="h-4 w-4" />
            Faiz Takvimi
            {faizTakvimi.length > 0 && (
              <Badge variant="secondary" className="ml-1">{faizTakvimi.length}</Badge>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => setKrediModalOpen(true)}
            className="gap-2 border-rose-200 text-rose-700 hover:bg-rose-50"
            data-testid="open-krediler-btn"
            disabled={!selectedBranchId}
          >
            <Banknote className="h-4 w-4" />
            Çekilen Krediler
            {krediRows.length > 0 && (
              <Badge variant="secondary" className="ml-1">{krediRows.length}</Badge>
            )}
          </Button>
        </div>
      </div>

      {/* Şube & Etiket Seçim */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="h-5 w-5 text-indigo-600" />
            Şube & Para Giriş Etiketi
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Şube Seçin</label>
            <Select value={selectedBranchId} onValueChange={setSelectedBranchId}>
              <SelectTrigger className="max-w-md" data-testid="branch-select">
                <SelectValue placeholder="Şube seçin..." />
              </SelectTrigger>
              <SelectContent>
                {branches.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedBranchId && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Para Giriş Etiketleri
                <span className="text-xs text-gray-500 ml-2">(Seçilmeyen etiketlere ait girişler tabloda gösterilmez)</span>
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="outline"
                  onClick={openEtiketModal}
                  className="gap-2"
                  data-testid="open-etiket-modal-btn"
                >
                  <Tag className="h-4 w-4" />
                  Para Giriş Etiketi Seç
                  {selectedEtiketIds.length > 0 && (
                    <Badge variant="secondary" className="ml-1">{selectedEtiketIds.length}</Badge>
                  )}
                </Button>
                {selectedEtiketObjects.map(e => (
                  <Badge key={e.id} variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 gap-1">
                    <Tag className="h-3 w-3" />
                    {e.kayit_adi}
                  </Badge>
                ))}
                {selectedEtiketIds.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearEtiketSelection}
                    className="text-red-600 hover:text-red-700"
                    data-testid="clear-etiket-btn"
                  >
                    Temizle
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* İçerik */}
      {!selectedBranchId ? (
        <div className="text-center py-16 text-gray-500">
          <Building2 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg">Faiz hesaplaması için bir şube seçin</p>
        </div>
      ) : loading ? (
        <div className="text-center py-12 text-gray-500">Yükleniyor...</div>
      ) : !data ? (
        <div className="text-center py-12 text-gray-500">Veri bulunamadı</div>
      ) : (
        <>
          {/* Özet Kartları */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">
                      Toplam Giriş
                      {selectedEtiketIds.length === 0 && (
                        <span className="block text-xs text-gray-400">Etiket seçilmedi</span>
                      )}
                    </p>
                    <p className="text-2xl font-bold text-green-700" data-testid="total-giris">
                      {formatCurrency(data.toplam_giris)}
                    </p>
                  </div>
                  <TrendingUp className="h-10 w-10 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Toplam Çıkış</p>
                    <p className="text-2xl font-bold text-red-700" data-testid="total-cikis">
                      {formatCurrency(data.toplam_cikis)}
                    </p>
                  </div>
                  <TrendingDown className="h-10 w-10 text-red-500" />
                </div>
              </CardContent>
            </Card>
            <Card className={`border-l-4 ${data.net_bakiye >= 0 ? 'border-l-emerald-500' : 'border-l-amber-500'}`}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Net Bakiye</p>
                    <p className={`text-2xl font-bold ${data.net_bakiye >= 0 ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="net-balance">
                      {formatCurrency(data.net_bakiye)}
                    </p>
                  </div>
                  <Wallet className={`h-10 w-10 ${data.net_bakiye >= 0 ? 'text-emerald-500' : 'text-amber-500'}`} />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filtre & Liste */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <span>{data.sube_adi} — Para Hareketleri</span>
                  <Badge variant="outline">{filtered.length}</Badge>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-36" data-testid="type-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tümü</SelectItem>
                      <SelectItem value="giris">Sadece Giriş</SelectItem>
                      <SelectItem value="cikis">Sadece Çıkış</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder="Cari hesap, etiket, açıklama..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-10"
                      data-testid="faiz-search"
                    />
                  </div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filtered.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  Bu şube için hareket bulunamadı
                  {selectedEtiketIds.length === 0 && (
                    <p className="text-sm mt-2">Para girişlerini listelemek için etiket seçebilirsiniz.</p>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Tip</TableHead>
                        <TableHead>Seri No</TableHead>
                        <TableHead>Cari Hesap</TableHead>
                        <TableHead>Etiket</TableHead>
                        <TableHead>Hesap</TableHead>
                        <TableHead className="text-right">Tutar</TableHead>
                        <TableHead className="text-right">Kümülatif Çıkış</TableHead>
                        <TableHead className="text-right">Kümülatif Giriş</TableHead>
                        <TableHead className="text-right">Kasa Net</TableHead>
                        <TableHead className="text-right">Faiz</TableHead>
                        <TableHead className="text-right">Kümülatif Faiz</TableHead>
                        <TableHead>Açıklama</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rowsWithCumulative.map((t) => (
                        t.is_empty_day ? (
                          <TableRow key={t.rowKey} className="bg-gray-50/50" data-testid={`empty-day-${t.tarih}`}>
                            <TableCell className="text-sm whitespace-nowrap text-gray-500">
                              {formatDate(t.tarih)}
                            </TableCell>
                            <TableCell colSpan={5} className="text-xs italic text-gray-400">
                              — Hareketsiz gün —
                            </TableCell>
                            <TableCell className="text-right text-gray-300">—</TableCell>
                            <TableCell className="text-right whitespace-nowrap font-semibold text-red-700">
                              {t.kumulatif_cikis > 0 ? `− ${formatCurrency(t.kumulatif_cikis)}` : formatCurrency(0)}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap font-semibold text-green-700">
                              {t.kumulatif_giris > 0 ? `+ ${formatCurrency(t.kumulatif_giris)}` : formatCurrency(0)}
                            </TableCell>
                            <TableCell className={`text-right whitespace-nowrap font-semibold ${t.kasa_net >= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                              {t.kasa_net >= 0 ? formatCurrency(t.kasa_net) : `− ${formatCurrency(Math.abs(t.kasa_net))}`}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              {t.is_last_of_day && t.faiz > 0 ? (
                                <span className="font-semibold text-fuchsia-700" title={`Günlük oran: %${Number(t.faiz_oran || 0).toFixed(4)} • Aktif kredilerin güncel ana para toplamı: ${formatCurrency(Math.abs(t.faiz_bazi || 0))}`}>
                                  {formatCurrency(t.faiz)}
                                </span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap font-semibold text-fuchsia-800">
                              {t.kumulatif_faiz > 0 ? formatCurrency(t.kumulatif_faiz) : <span className="text-gray-300">—</span>}
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        ) : (
                          <TableRow
                            key={t.rowKey}
                            data-testid={`tx-row-${t.id}`}
                            className={t.is_virtual_faiz_cikis ? 'bg-purple-50/60 border-l-4 border-l-purple-400' : ''}
                          >
                            <TableCell className="text-sm whitespace-nowrap">
                              {formatDate(t.tarih)}
                              {t.saat && <span className="text-gray-400 ml-1">{t.saat}</span>}
                            </TableCell>
                            <TableCell>
                              {t.is_virtual_faiz_cikis ? (
                                <Badge className="bg-purple-100 text-purple-700 border-purple-300 gap-1">
                                  <Percent className="h-3 w-3" />
                                  Faiz Devri
                                </Badge>
                              ) : t.tip === 'giris' ? (
                                <Badge className="bg-green-100 text-green-700 border-green-300 gap-1">
                                  <ArrowDownCircle className="h-3 w-3" />
                                  Giriş
                                </Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-700 border-red-300 gap-1">
                                  <ArrowUpCircle className="h-3 w-3" />
                                  Çıkış
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-gray-500">{t.seri_no || '-'}</TableCell>
                            <TableCell className="text-sm">{t.cari_hesap_adi || '-'}</TableCell>
                            <TableCell className="text-sm">
                              {t.etiket_adi ? (
                                <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 gap-1">
                                  <Tag className="h-3 w-3" />
                                  {t.etiket_adi}
                                </Badge>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm">{t.hesap_adi || '-'}</TableCell>
                            <TableCell className={`text-right font-medium whitespace-nowrap ${t.tip === 'giris' ? 'text-green-700' : 'text-red-700'}`}>
                              {t.tip === 'giris' ? '+' : '−'} {formatCurrency(t.miktar, t.para_birimi)}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap font-semibold text-red-700">
                              {t.kumulatif_cikis > 0 ? `− ${formatCurrency(t.kumulatif_cikis)}` : formatCurrency(0)}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap font-semibold text-green-700">
                              {t.kumulatif_giris > 0 ? `+ ${formatCurrency(t.kumulatif_giris)}` : formatCurrency(0)}
                            </TableCell>
                            <TableCell className={`text-right whitespace-nowrap font-semibold ${t.kasa_net >= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                              {t.kasa_net >= 0 ? formatCurrency(t.kasa_net) : `− ${formatCurrency(Math.abs(t.kasa_net))}`}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              {t.is_last_of_day && t.faiz > 0 ? (
                                <span className="font-semibold text-fuchsia-700" title={`Günlük oran: %${Number(t.faiz_oran || 0).toFixed(4)} • Aktif kredilerin güncel ana para toplamı: ${formatCurrency(Math.abs(t.faiz_bazi || 0))}`}>
                                  {formatCurrency(t.faiz)}
                                </span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap font-semibold text-fuchsia-800">
                              {t.kumulatif_faiz > 0 ? formatCurrency(t.kumulatif_faiz) : <span className="text-gray-300">—</span>}
                            </TableCell>
                            <TableCell className="text-sm text-gray-600 max-w-xs truncate" title={t.aciklama}>
                              {t.aciklama || (t.job_konu ? <span className="italic text-gray-500">İş: {t.job_konu}</span> : '-')}
                            </TableCell>
                          </TableRow>
                        )
                      ))}
                      <TableRow className="bg-slate-100 border-t-2 border-slate-300 font-bold">
                        <TableCell colSpan={6} className="text-right uppercase text-xs tracking-wide text-slate-700">
                          Toplam
                        </TableCell>
                        <TableCell />
                        <TableCell className="text-right text-red-700 whitespace-nowrap" data-testid="footer-toplam-cikis">
                          {grandTotalCikis > 0 ? `− ${formatCurrency(grandTotalCikis)}` : formatCurrency(0)}
                        </TableCell>
                        <TableCell className="text-right text-green-700 whitespace-nowrap" data-testid="footer-toplam-giris">
                          {grandTotalGiris > 0 ? `+ ${formatCurrency(grandTotalGiris)}` : formatCurrency(0)}
                        </TableCell>
                        <TableCell className={`text-right whitespace-nowrap ${grandKasaNet >= 0 ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="footer-kasa-net">
                          {grandKasaNet >= 0 ? formatCurrency(grandKasaNet) : `− ${formatCurrency(Math.abs(grandKasaNet))}`}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap text-fuchsia-700" data-testid="footer-toplam-faiz">
                          {grandTotalFaiz > 0 ? formatCurrency(grandTotalFaiz) : '—'}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap text-fuchsia-800" data-testid="footer-kumulatif-faiz">
                          {grandTotalFaiz > 0 ? formatCurrency(grandTotalFaiz) : '—'}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Etiket Seçim Modalı */}
      <Dialog open={etiketModalOpen} onOpenChange={setEtiketModalOpen}>
        <DialogContent className="max-w-md max-h-[80vh] flex flex-col" data-testid="etiket-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-indigo-600" />
              Para Giriş Etiketi Seç
            </DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 py-3">
            {etiketler.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                Henüz etiket tanımlanmamış
              </div>
            ) : (
              <div className="space-y-1">
                {etiketler.map((e) => {
                  const checked = pendingEtiketIds.includes(e.id);
                  return (
                    <label
                      key={e.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors ${
                        checked ? 'bg-indigo-50' : 'hover:bg-gray-50'
                      }`}
                      data-testid={`etiket-option-${e.id}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleEtiket(e.id)}
                      />
                      <Tag className="h-4 w-4 text-indigo-600" />
                      <span className="flex-1 text-sm">{e.kayit_adi}</span>
                      {checked && <Check className="h-4 w-4 text-indigo-600" />}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-3">
            <div className="flex items-center justify-between w-full">
              <span className="text-sm text-gray-500">
                {pendingEtiketIds.length} etiket seçildi
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEtiketModalOpen(false)}>
                  İptal
                </Button>
                <Button
                  onClick={applyEtiketSelection}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="apply-etiket-btn"
                >
                  Uygula
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Faiz Takvimi Modalı */}
      <Dialog open={faizModalOpen} onOpenChange={setFaizModalOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col" data-testid="faiz-takvimi-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-indigo-600" />
              Faiz Takvimi — Aylık Oranlar
            </DialogTitle>
          </DialogHeader>

          {/* Yeni Kayıt Formu */}
          <div className="border rounded-lg p-4 bg-indigo-50/40 space-y-3">
            <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-1">
              <Plus className="h-4 w-4" />
              Yeni Aylık Faiz Oranı Ekle
            </h3>
            <div className="text-xs text-gray-600 bg-white/80 rounded px-3 py-2 border border-indigo-100">
              <span className="font-medium text-indigo-700">Takvimden tarih aralığı seçin:</span> İlk tıklama başlangıç, ikinci tıklama bitiş tarihidir. Aradaki tüm günler seçili sayılır.
            </div>

            <div className="flex flex-col lg:flex-row gap-4">
              {/* Range Calendar */}
              <div className="flex justify-center lg:justify-start">
                <div className="rounded-md border bg-white shadow-sm">
                  <Calendar
                    mode="range"
                    numberOfMonths={1}
                    selected={faizRange}
                    onSelect={setFaizRange}
                    locale={tr}
                    weekStartsOn={1}
                    data-testid="faiz-calendar"
                  />
                </div>
              </div>

              {/* Right panel — selection + inputs */}
              <div className="flex-1 space-y-3">
                <div className="rounded-md border bg-white p-3">
                  <div className="text-xs text-gray-500 mb-1">Seçilen Aralık</div>
                  {faizRange?.from && faizRange?.to ? (
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold text-indigo-700">
                        {formatDate(toIsoDate(faizRange.from))} → {formatDate(toIsoDate(faizRange.to))}
                      </div>
                      <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                        {Math.round((faizRange.to - faizRange.from) / 86400000) + 1} gün
                      </Badge>
                    </div>
                  ) : faizRange?.from ? (
                    <div className="text-sm text-gray-600">
                      Başlangıç: {formatDate(toIsoDate(faizRange.from))} — <span className="text-amber-600">bitiş tarihini tıklayın</span>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400 italic">Takvimden başlangıç tarihini tıklayın</div>
                  )}
                </div>

                <div>
                  <label className="text-xs text-gray-600">Aylık Faiz Oranı (%) *</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={faizAylikOran}
                    onChange={(e) => setFaizAylikOran(e.target.value)}
                    placeholder="Örn. 4.5"
                    data-testid="faiz-aylik-oran"
                  />
                  {faizAylikOran && parseFloat(faizAylikOran) > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Günlük ≈ %{(parseFloat(faizAylikOran) / 30).toFixed(4)}
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-xs text-gray-600">Açıklama</label>
                  <Input
                    value={faizAciklama}
                    onChange={(e) => setFaizAciklama(e.target.value)}
                    placeholder="Opsiyonel not"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  {(faizRange?.from || faizRange?.to) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFaizRange(undefined)}
                      className="text-gray-600"
                    >
                      Temizle
                    </Button>
                  )}
                  <Button
                    onClick={saveFaizEntry}
                    disabled={faizSaving}
                    className="bg-indigo-600 hover:bg-indigo-700 gap-2"
                    data-testid="save-faiz-btn"
                  >
                    <Plus className="h-4 w-4" />
                    {faizSaving ? 'Kaydediliyor...' : 'Kaydet'}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Mevcut Kayıtlar */}
          <div className="overflow-y-auto flex-1 py-3">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Tanımlı Oranlar</h3>
            {faizTakvimi.length === 0 ? (
              <div className="text-center text-gray-500 py-6 text-sm">
                Henüz faiz oranı tanımlanmamış
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Başlangıç</TableHead>
                      <TableHead className="text-xs">Bitiş</TableHead>
                      <TableHead className="text-xs text-right">Aylık (%)</TableHead>
                      <TableHead className="text-xs text-right">Günlük (%)</TableHead>
                      <TableHead className="text-xs">Açıklama</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {faizTakvimi.map((item) => (
                      <TableRow key={item.id} data-testid={`faiz-row-${item.id}`}>
                        <TableCell className="text-xs">{formatDate(item.baslangic_tarihi)}</TableCell>
                        <TableCell className="text-xs">{formatDate(item.bitis_tarihi)}</TableCell>
                        <TableCell className="text-xs text-right font-semibold text-indigo-700">
                          %{Number(item.aylik_oran).toFixed(2)}
                        </TableCell>
                        <TableCell className="text-xs text-right text-gray-600">
                          %{Number(item.gunluk_oran).toFixed(4)}
                        </TableCell>
                        <TableCell className="text-xs text-gray-600">{item.aciklama || '-'}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteFaizEntry(item.id)}
                            className="text-red-500 hover:text-red-700 h-7 w-7 p-0"
                            data-testid={`delete-faiz-${item.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-3">
            <Button variant="outline" onClick={() => setFaizModalOpen(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Çekilen Krediler Modalı */}
      <Dialog open={krediModalOpen} onOpenChange={setKrediModalOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="krediler-modal">
          <DialogHeader>
            <div className="flex items-center justify-between gap-2 pr-6">
              <DialogTitle className="flex items-center gap-2">
                <Banknote className="h-5 w-5 text-rose-600" />
                Çekilen Krediler — Günlük Harcama Bazlı
              </DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={exportSummaryToExcel}
                disabled={krediRows.length === 0}
                className="gap-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="export-kredi-ozet-btn"
              >
                <FileSpreadsheet className="h-4 w-4" />
                Özeti Excel İndir
              </Button>
            </div>
          </DialogHeader>

          <div className="text-xs text-gray-600 bg-rose-50 border border-rose-100 rounded px-3 py-2 mb-2">
            <span className="font-medium text-rose-700">Mantık:</span> Kasa bakiyesi eksideyken yapılan her günün toplam harcaması, o gün bankadan çekilen kredi olarak kabul edilir. Kasa net ≥ 0 olan günler listeye dahil edilmez.
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="border rounded-lg p-3 bg-rose-50/50">
              <div className="text-xs text-gray-500">Toplam Gün</div>
              <div className="text-xl font-bold text-rose-700" data-testid="kredi-gun-sayisi">
                {krediRows.length} gün
              </div>
            </div>
            <div className="border rounded-lg p-3 bg-rose-50/50">
              <div className="text-xs text-gray-500">Toplam Çekilen Kredi</div>
              <div className="text-xl font-bold text-rose-700" data-testid="kredi-toplam">
                {formatCurrency(toplamCekilenKredi)}
              </div>
            </div>
          </div>

          <div className="overflow-y-auto flex-1 border rounded-lg">
            {krediRows.length === 0 ? (
              <div className="text-center py-12 text-gray-500 text-sm">
                {selectedBranchId
                  ? 'Kasa eksiye düşen harcama günü bulunamadı'
                  : 'Önce bir şube seçin'}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kredi No</TableHead>
                    <TableHead>Kredi Çekilme Tarihi</TableHead>
                    <TableHead>Kredi Son Ödeme Tarihi</TableHead>
                    <TableHead className="text-right">Ana Para (Çekilen Kredi)</TableHead>
                    <TableHead className="text-right">Faiz İşleyen Kısım</TableHead>
                    <TableHead className="text-right">İşleyen Faiz</TableHead>
                    <TableHead className="text-center">Devir Sayısı</TableHead>
                    <TableHead className="text-center">Kredi Durumu</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {krediRows.map(row => {
                    const sonOdemeIso = row.son_odeme_tarihi;
                    const rowKey = row.tarih;
                    const isExpanded = expandedKrediDate === rowKey;

                    // Günlük faiz listesi (aylık rollover dahil)
                    const dailyFaizList = isExpanded ? buildDailyFaizList(row.tarih, row.baslangic_ana_para) : [];

                    return (
                      <React.Fragment key={rowKey}>
                        <TableRow
                          data-testid={`kredi-row-${rowKey}`}
                          className="cursor-pointer hover:bg-rose-50/50 transition-colors"
                          onClick={() => setExpandedKrediDate(isExpanded ? null : rowKey)}
                        >
                          <TableCell className="text-sm whitespace-nowrap font-semibold text-rose-800" data-testid={`kredi-no-${rowKey}`}>
                            #{row.kredi_no}
                          </TableCell>
                          <TableCell className="text-sm whitespace-nowrap">
                            <div className="flex items-center gap-1">
                              {isExpanded
                                ? <ChevronDown className="h-3.5 w-3.5 text-rose-600" />
                                : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />}
                              {formatDate(row.tarih)}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm whitespace-nowrap">
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                              {formatDate(sonOdemeIso)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-semibold text-rose-700 whitespace-nowrap">
                            {formatCurrency(row.toplam_harcama)}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-amber-700 whitespace-nowrap" data-testid={`kredi-faiz-isleyen-kisim-${rowKey}`}>
                            {row.faiz_isleyen_kisim > 0 ? formatCurrency(row.faiz_isleyen_kisim) : <span className="text-gray-300">—</span>}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-fuchsia-700 whitespace-nowrap" data-testid={`kredi-isleyen-faiz-${rowKey}`}>
                            {row.isleyen_faiz > 0 ? formatCurrency(row.isleyen_faiz) : <span className="text-gray-300">—</span>}
                          </TableCell>
                          <TableCell className="text-center whitespace-nowrap" data-testid={`kredi-devir-sayisi-${rowKey}`}>
                            {row.devir_sayisi > 0 ? (
                              <Badge className="bg-indigo-100 text-indigo-700 border-indigo-300">{row.devir_sayisi} devir</Badge>
                            ) : (
                              <span className="text-gray-300 text-xs">0</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center whitespace-nowrap" data-testid={`kredi-durumu-${rowKey}`}>
                            {row.kredi_durumu === 'kapatildi' ? (
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">Kapatıldı</Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-700 border-amber-300">Açık</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow className="bg-rose-50/30">
                            <TableCell colSpan={8} className="p-0">
                              <div className="px-6 py-3">
                                <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                                  <div className="text-xs font-semibold text-rose-800 uppercase tracking-wide flex items-center gap-2">
                                    <CalendarDays className="h-4 w-4" />
                                    Günlük Faiz Dökümü — Başlangıç {formatCurrency(row.baslangic_ana_para)}
                                    {row.faiz_isleyen_kisim > row.baslangic_ana_para && (
                                      <span className="text-indigo-700 normal-case tracking-normal">
                                        → Güncel {formatCurrency(row.faiz_isleyen_kisim)}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <div className="text-xs text-gray-600">
                                      {(() => {
                                        const dayRows = dailyFaizList.filter(x => !x.is_rollover);
                                        const rolloverCount = dailyFaizList.length - dayRows.length;
                                        if (dayRows.length === 0) {
                                          return `${formatDate(row.tarih)} → ${formatDate(sonOdemeIso)}`;
                                        }
                                        const firstT = dayRows[0].tarih;
                                        const lastT = dayRows[dayRows.length - 1].tarih;
                                        return `${formatDate(firstT)} → ${formatDate(lastT)} • ${dayRows.length} gün${rolloverCount > 0 ? ` • ${rolloverCount} devir` : ''}`;
                                      })()}
                                    </div>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={(e) => { e.stopPropagation(); exportCreditDetailToExcel(row); }}
                                      className="gap-1.5 h-7 px-2 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                                      data-testid={`export-kredi-detay-${rowKey}`}
                                    >
                                      <FileSpreadsheet className="h-3.5 w-3.5" />
                                      Excel İndir
                                    </Button>
                                  </div>
                                </div>
                                {dailyFaizList.length === 0 ? (
                                  <div className="text-sm text-gray-500 italic">Gün listesi oluşturulamadı</div>
                                ) : (
                                  <div className="rounded border border-rose-100 overflow-hidden bg-white">
                                    <Table>
                                      <TableHeader>
                                        <TableRow className="bg-rose-50">
                                          <TableHead className="text-xs">Tarih</TableHead>
                                          <TableHead className="text-xs">Dönem</TableHead>
                                          <TableHead className="text-xs text-right">Ana Para (O Günkü)</TableHead>
                                          <TableHead className="text-xs text-right">Günlük Oran</TableHead>
                                          <TableHead className="text-xs text-right">Günlük Faiz</TableHead>
                                          <TableHead className="text-xs text-right">Kümülatif Faiz</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {dailyFaizList.map(d => (
                                          d.is_rollover ? (
                                            <TableRow key={d.key} className="bg-indigo-50/70 border-y border-indigo-200">
                                              <TableCell colSpan={6} className="text-xs py-2">
                                                <div className="flex items-center gap-2 text-indigo-800">
                                                  <TrendingUp className="h-4 w-4" />
                                                  <span className="font-semibold">
                                                    {d.donem}. Dönem Tamamlandı ({formatDate(d.tarih)})
                                                  </span>
                                                  <span className="text-indigo-700">
                                                    — Faiz ana paraya eklendi: {formatCurrency(d.eski_ana_para)} + {formatCurrency(d.eklenen_faiz)} = <strong>{formatCurrency(d.yeni_ana_para)}</strong>
                                                  </span>
                                                  <span className="ml-auto text-[11px] text-indigo-600">
                                                    {d.yeni_donem}. Dönem başlıyor: {formatDate(d.yeni_donem_baslangic)}
                                                  </span>
                                                </div>
                                              </TableCell>
                                            </TableRow>
                                          ) : (
                                            <TableRow key={d.key}>
                                              <TableCell className="text-xs whitespace-nowrap">{formatDate(d.tarih)}</TableCell>
                                              <TableCell className="text-xs whitespace-nowrap">
                                                <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">
                                                  D{d.donem}
                                                </Badge>
                                              </TableCell>
                                              <TableCell className="text-xs text-right text-amber-800 whitespace-nowrap font-medium">
                                                {formatCurrency(d.ana_para)}
                                              </TableCell>
                                              <TableCell className="text-xs text-right text-gray-600 whitespace-nowrap">
                                                {d.oran > 0 ? `%${Number(d.oran).toFixed(4)}` : '—'}
                                              </TableCell>
                                              <TableCell className="text-xs text-right font-medium text-fuchsia-700 whitespace-nowrap">
                                                {d.gunluk_faiz > 0 ? formatCurrency(d.gunluk_faiz) : '—'}
                                              </TableCell>
                                              <TableCell className="text-xs text-right font-semibold text-fuchsia-800 whitespace-nowrap">
                                                {formatCurrency(d.kumulatif_faiz)}
                                              </TableCell>
                                            </TableRow>
                                          )
                                        ))}
                                        <TableRow className="bg-rose-50/70 font-bold">
                                          <TableCell colSpan={5} className="text-xs text-right uppercase text-rose-800">
                                            Bu Kredinin Toplam Faizi
                                          </TableCell>
                                          <TableCell className="text-xs text-right text-fuchsia-800 whitespace-nowrap">
                                            {formatCurrency(
                                              dailyFaizList
                                                .filter(x => !x.is_rollover)
                                                .reduce((mx, x) => Math.max(mx, Number(x.kumulatif_faiz || 0)), 0)
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      </TableBody>
                                    </Table>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                  <TableRow className="bg-rose-100 border-t-2 border-rose-300 font-bold">
                    <TableCell colSpan={3} className="text-right uppercase text-xs tracking-wide text-rose-800">
                      Toplam
                    </TableCell>
                    <TableCell className="text-right text-rose-800 whitespace-nowrap" data-testid="kredi-footer-toplam">
                      {formatCurrency(toplamCekilenKredi)}
                    </TableCell>
                    <TableCell className="text-right text-amber-800 whitespace-nowrap" data-testid="kredi-footer-faiz-isleyen-kisim">
                      {toplamFaizIsleyenKisim > 0 ? formatCurrency(toplamFaizIsleyenKisim) : '—'}
                    </TableCell>
                    <TableCell className="text-right text-fuchsia-800 whitespace-nowrap" data-testid="kredi-footer-isleyen-faiz">
                      {toplamIsleyenFaiz > 0 ? formatCurrency(toplamIsleyenFaiz) : '—'}
                    </TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </div>

          <DialogFooter className="border-t pt-3">
            <Button variant="outline" onClick={() => setKrediModalOpen(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BoardBirimFaizHesaplama;
